import type { CSSProperties, ReactNode } from 'react'

/**
 * Design-language icon set — 24×24 grid, 1.75 stroke, rounded caps/joins.
 *
 * This is the *richer* icon set used for app chrome (sidenav, topbar,
 * page-level buttons, empty states). It's deliberately separate from
 * `Icon` (a tight 16-grid set used inside widget bodies) because the two
 * have different visual weight: `ItIcon` reads as UI furniture, `Icon`
 * reads as inline glyph.
 *
 * `IT_ICON_PATHS` is the single source of truth — the union of its keys
 * becomes `ItIconName` so TypeScript catches typos at call sites.
 */

// All icons share: rounded joins, ~10-14 unit outer footprint centered on 12,12
const IT_ICON_PATHS = {
  // === Nav ===
  dashboard: (
    <>
      <rect x="3.5" y="3.5" width="7" height="9" rx="1.5" />
      <rect x="13.5" y="3.5" width="7" height="5" rx="1.5" />
      <rect x="13.5" y="11.5" width="7" height="9" rx="1.5" />
      <rect x="3.5" y="15.5" width="7" height="5" rx="1.5" />
    </>
  ),
  identity: (
    <>
      <circle cx="12" cy="8.5" r="3.5" />
      <path d="M5.5 20c0-3.6 2.9-6.5 6.5-6.5s6.5 2.9 6.5 6.5" />
    </>
  ),
  analytics: (
    <>
      <path d="M4 20V10" />
      <path d="M10 20V4" />
      <path d="M16 20v-7" />
      <path d="M22 20H2" />
    </>
  ),
  capture: (
    <>
      <rect x="3" y="6" width="18" height="14" rx="2" />
      <path d="M8 6V4.5A1.5 1.5 0 0 1 9.5 3h5A1.5 1.5 0 0 1 16 4.5V6" />
      <circle cx="12" cy="13" r="3.5" />
    </>
  ),
  shield: <path d="M12 3 4.5 6v5.5c0 4.6 3.1 8.2 7.5 9.5 4.4-1.3 7.5-4.9 7.5-9.5V6L12 3Z" />,
  lock: (
    <>
      <rect x="4.5" y="10.5" width="15" height="10" rx="2" />
      <path d="M8 10.5V7.5a4 4 0 0 1 8 0v3" />
    </>
  ),
  key: (
    <>
      <circle cx="7.5" cy="14.5" r="4" />
      <path d="M11 12.5 20 3.5" />
      <path d="M16.5 7l2.5 2.5" />
      <path d="M14 9.5l2 2" />
    </>
  ),
  cloud: <path d="M7 18h10a4 4 0 0 0 .6-7.95 6 6 0 0 0-11.5 1.45A3.5 3.5 0 0 0 7 18Z" />,
  database: (
    <>
      <ellipse cx="12" cy="5.5" rx="7" ry="2.5" />
      <path d="M5 5.5v6c0 1.38 3.13 2.5 7 2.5s7-1.12 7-2.5v-6" />
      <path d="M5 11.5v6c0 1.38 3.13 2.5 7 2.5s7-1.12 7-2.5v-6" />
    </>
  ),
  // === Actions ===
  plus: (
    <>
      <path d="M12 5v14" />
      <path d="M5 12h14" />
    </>
  ),
  search: (
    <>
      <circle cx="10.5" cy="10.5" r="6" />
      <path d="M15 15l5 5" />
    </>
  ),
  filter: <path d="M4 5h16l-6 8v6l-4-2v-4L4 5Z" />,
  download: (
    <>
      <path d="M12 4v12" />
      <path d="M6.5 10.5 12 16l5.5-5.5" />
      <path d="M4 20h16" />
    </>
  ),
  upload: (
    <>
      <path d="M12 20V8" />
      <path d="M6.5 13.5 12 8l5.5 5.5" />
      <path d="M4 4h16" />
    </>
  ),
  refresh: (
    <>
      <path d="M20 11a8 8 0 1 0-2.1 6.2" />
      <path d="M20 5v6h-6" />
    </>
  ),
  edit: (
    <>
      <path d="M4 20h4.5L19.5 9l-4.5-4.5L4 15.5V20Z" />
      <path d="M14 5l4.5 4.5" />
    </>
  ),
  trash: (
    <>
      <path d="M4.5 7h15" />
      <path d="M9 7V5a1.5 1.5 0 0 1 1.5-1.5h3A1.5 1.5 0 0 1 15 5v2" />
      <path d="M6 7l1 12.5a1.5 1.5 0 0 0 1.5 1.5h7a1.5 1.5 0 0 0 1.5-1.5L18 7" />
    </>
  ),
  copy: (
    <>
      <rect x="8" y="8" width="12" height="12" rx="2" />
      <path d="M16 8V5.5A1.5 1.5 0 0 0 14.5 4h-10A1.5 1.5 0 0 0 3 5.5v10A1.5 1.5 0 0 0 4.5 17H7" />
    </>
  ),
  // === Status ===
  check: <path d="M4.5 12.5 9.5 17.5 20 7" />,
  circleCheck: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M8 12.5 11 15.5 16.5 9.5" />
    </>
  ),
  circleAlert: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7.5v5" />
      <path d="M12 16h.01" />
    </>
  ),
  triangleAlert: (
    <>
      <path d="M12 4 3 20h18L12 4Z" />
      <path d="M12 10v4" />
      <path d="M12 17h.01" />
    </>
  ),
  info: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 10.5v6" />
      <path d="M12 7.5h.01" />
    </>
  ),
  x: (
    <>
      <path d="M6 6l12 12" />
      <path d="M18 6 6 18" />
    </>
  ),
  // === Directional ===
  chevronDown: <path d="m6 9 6 6 6-6" />,
  chevronRight: <path d="m9 6 6 6-6 6" />,
  chevronLeft: <path d="m15 6-6 6 6 6" />,
  chevronUp: <path d="m6 15 6-6 6 6" />,
  arrowRight: (
    <>
      <path d="M4 12h16" />
      <path d="m14 6 6 6-6 6" />
    </>
  ),
  arrowUpRight: (
    <>
      <path d="M6 18 18 6" />
      <path d="M8 6h10v10" />
    </>
  ),
  // === Domain: security / IAM ===
  user: (
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M5 20c0-3.87 3.13-7 7-7s7 3.13 7 7" />
    </>
  ),
  users: (
    <>
      <circle cx="9.5" cy="9" r="3.5" />
      <path d="M3.5 20c0-3.3 2.7-6 6-6s6 2.7 6 6" />
      <circle cx="16.5" cy="7.5" r="2.5" />
      <path d="M15 13.8a5 5 0 0 1 5.5 6.2" />
    </>
  ),
  userPlus: (
    <>
      <circle cx="10" cy="8" r="3.5" />
      <path d="M3.5 20c0-3.6 2.9-6.5 6.5-6.5s6.5 2.9 6.5 6.5" />
      <path d="M19 7v6" />
      <path d="M16 10h6" />
    </>
  ),
  serviceAccount: (
    <>
      <rect x="4" y="4" width="16" height="16" rx="3" />
      <path d="M8 10.5h5" />
      <path d="M8 14h8" />
      <circle cx="16" cy="10.5" r="1" fill="currentColor" />
    </>
  ),
  role: (
    <>
      <path d="M12 3 4 6v5c0 4.5 3.2 8.3 8 9 4.8-.7 8-4.5 8-9V6l-8-3Z" />
      <path d="M9 11.5 11 13.5 15.5 9" />
    </>
  ),
  fingerprint: (
    <>
      <path d="M6 13c0-3.3 2.7-6 6-6s6 2.7 6 6" />
      <path d="M9 13c0-1.7 1.3-3 3-3s3 1.3 3 3v2" />
      <path d="M12 14v4" />
      <path d="M6 17c0 1.5-.4 2.9-1 4" />
      <path d="M19 17c0 1.5.4 2.9 1 4" />
    </>
  ),
  // === Domain: infra / logs ===
  server: (
    <>
      <rect x="3.5" y="4" width="17" height="7" rx="1.5" />
      <rect x="3.5" y="13" width="17" height="7" rx="1.5" />
      <path d="M7 7.5h.01" />
      <path d="M7 16.5h.01" />
    </>
  ),
  activity: <path d="M3 12h3l3-8 6 16 3-8h3" />,
  eye: (
    <>
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  eyeOff: (
    <>
      <path d="M3 3l18 18" />
      <path d="M10.5 6.3A10 10 0 0 1 22 12a18.7 18.7 0 0 1-2.8 3.5" />
      <path d="M6.3 6.3A18.7 18.7 0 0 0 2 12s3.5 7 10 7c1.9 0 3.6-.5 5-1.3" />
      <path d="M9.9 9.9A3 3 0 0 0 14.1 14.1" />
    </>
  ),
  bell: (
    <>
      <path d="M6 16V11a6 6 0 0 1 12 0v5l1.5 2H4.5L6 16Z" />
      <path d="M10 20a2 2 0 0 0 4 0" />
    </>
  ),
  settings: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 3v2M12 19v2M3 12h2M19 12h2M5.6 5.6l1.4 1.4M17 17l1.4 1.4M5.6 18.4 7 17M17 7l1.4-1.4" />
    </>
  ),
  // === Domain: data / time ===
  clock: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </>
  ),
  calendar: (
    <>
      <rect x="3.5" y="5" width="17" height="15" rx="2" />
      <path d="M3.5 10h17" />
      <path d="M8 3v4M16 3v4" />
    </>
  ),
  trending: (
    <>
      <path d="M3 17 10 10l4 4 7-7" />
      <path d="M14 7h7v7" />
    </>
  ),
  chart: (
    <>
      <path d="M4 20V4" />
      <path d="M4 20h16" />
      <rect x="7" y="12" width="3" height="6" rx="0.5" />
      <rect x="12" y="8" width="3" height="10" rx="0.5" />
      <rect x="17" y="14" width="3" height="4" rx="0.5" />
    </>
  ),
  pieChart: (
    <>
      <path d="M21 12A9 9 0 1 1 12 3v9h9Z" />
      <path d="M21 12a9 9 0 0 0-9-9v9h9Z" />
    </>
  ),
  // === Meta ===
  menu: (
    <>
      <path d="M4 7h16" />
      <path d="M4 12h16" />
      <path d="M4 17h16" />
    </>
  ),
  more: (
    <>
      <circle cx="5" cy="12" r="1" fill="currentColor" />
      <circle cx="12" cy="12" r="1" fill="currentColor" />
      <circle cx="19" cy="12" r="1" fill="currentColor" />
    </>
  ),
  link: (
    <>
      <path d="M10 14a4 4 0 0 0 5.66 0l3-3a4 4 0 0 0-5.66-5.66L12 6" />
      <path d="M14 10a4 4 0 0 0-5.66 0l-3 3a4 4 0 0 0 5.66 5.66L12 18" />
    </>
  ),
  logout: (
    <>
      <path d="M10 4h-5A1.5 1.5 0 0 0 3.5 5.5v13A1.5 1.5 0 0 0 5 20h5" />
      <path d="M16 8l4 4-4 4" />
      <path d="M20 12H10" />
    </>
  ),
} as const satisfies Record<string, ReactNode>

export type ItIconName = keyof typeof IT_ICON_PATHS

export const IT_ICON_NAMES = Object.keys(IT_ICON_PATHS) as ItIconName[]

export interface ItIconProps {
  name: ItIconName
  size?: number
  stroke?: number
  color?: string
  style?: CSSProperties
}

export function ItIcon({
  name,
  size = 22,
  stroke = 1.75,
  color = 'currentColor',
  style,
}: ItIconProps) {
  const paths = IT_ICON_PATHS[name]
  if (!paths) return null
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={stroke}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={style}
      aria-hidden="true"
    >
      {paths}
    </svg>
  )
}
